import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import InventariobulkModal from "./inventariobulkmodal";
import { generarPdfInventario, exportarExcelInventario } from "./reporteinventariomotores";
import { useMaterialUIController, setAnchoCodigoBarra, setAltoCodigoBarra } from "context";
import { useGlobalMDAlert } from "context";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { puedeAccion, ocultaCostos } from "lib/seguridad/permisosPorModulo";
import { FP, PageHeader, fpDataGridSx, fpInputSx } from "components/financepro";
import Barcode128 from "components/Barcode128";
import { imprimirHtml } from "lib/impresionhtml";
import { useApi } from "lib/api";
import { useMultiempresa, SelectorEmpresa, nombreEmpresa, useLogoEmpresa } from "lib/multiempresa";

// Escape mínimo para interpolar datos del chasis en el HTML de impresión.
const escHtml = (s) =>
  String(s ?? '').replace(/[<>&"]/g, (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[ch]));

const TARJETAS_POR_TANDA = 50;

// El autocomplete de chasis no virtualiza: con miles de opciones, abrirlo
// pintaba todas. Se muestran solo las primeras coincidencias de lo escrito.
const MAX_OPCIONES_CHASIS = 100;
const filtrarOpcionesChasis = (options, { inputValue }) => {
  const q = String(inputValue || '').trim().toUpperCase();
  const out = [];
  for (const o of options) {
    if (!q || String(o).toUpperCase().includes(q)) {
      out.push(o);
      if (out.length >= MAX_OPCIONES_CHASIS) break;
    }
  }
  return out;
};

// El endpoint /api/informemotores/inventario manda cada monto una sola vez;
// aquí se derivan los campos formateados (cost2/price2/mprice2, balance y
// proyectado de incautados) que antes venían del format() de MySQL.
const fmtMonto = (v) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';
};

function normalizarProducto(p) {
  const chasis = String(p.chasis || '').toUpperCase();
  return {
    ...p,
    chasis,
    id: chasis,
    cost2: fmtMonto(p.costo),
    price2: fmtMonto(p.precio),
    mprice2: fmtMonto(p.mprecio),
    cost: p.costo,
    price: p.precio,
    mprice: p.mprecio,
    ...(p.balanceactual !== undefined
      ? { balanceactual: p.balanceactual == null ? null : fmtMonto(p.balanceactual) }
      : {}),
    ...(p.proyectado !== undefined
      ? { proyectado: p.proyectado == null ? null : fmtMonto(p.proyectado) }
      : {}),
  };
}

function CustomToolbar({ seleccionados = 0, onImprimirEtiquetas }) {
  return (
    <GridToolbarContainer sx={{ p: 1, gap: 1 }}>
      <GridToolbarExport
        sx={{
          color: FP.color.navy,
          textTransform: 'none',
          fontWeight: 700,
        }}
      />
      <Button
        size="small"
        startIcon={<QrCode2RoundedIcon />}
        disabled={!seleccionados}
        onClick={onImprimirEtiquetas}
        sx={{
          color: FP.color.navy,
          textTransform: 'none',
          fontWeight: 700,
          '&.Mui-disabled': { color: FP.color.textSecondary },
        }}
      >
        {seleccionados ? `Imprimir etiquetas (${seleccionados})` : 'Imprimir etiquetas'}
      </Button>
    </GridToolbarContainer>
  );
}

function buildMotoresColumns(sucursalMap = {}, onBarcode, onEdit, multiempresa) {
  return [
    {
      field: '__barcode',
      headerName: '',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 56,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Tooltip title="Ver código de barras">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onBarcode && onBarcode(params.row);
            }}
            sx={{ color: FP.color.navy }}
          >
            <QrCode2RoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { field: 'id', headerName: 'Chasis', flex: 1, minWidth: 170 },
    {
      field: 'sucursal',
      headerName: 'Sucursal',
      type: 'string',
      flex: 0.9,
      minWidth: 150,
      valueGetter: (value, row) => {
        const code = row?.sucursal != null ? String(row.sucursal).trim() : '';
        if (!code) return '';
        return sucursalMap[code] || code;
      },
    },
    // Multi-empresa: empresa dueña del chasis (solo con el modo activo)
    ...(multiempresa && multiempresa.activo
      ? [
          {
            field: 'empresa',
            headerName: 'Empresa',
            flex: 0.7,
            minWidth: 120,
            valueGetter: (value, row) => nombreEmpresa(multiempresa.empresas, row.empresa || 1),
          },
        ]
      : []),
    { field: 'marca', headerName: 'Marca', flex: 1, minWidth: 120, editable: true },
    { field: 'modelo', headerName: 'Modelos', type: 'string', flex: 1, minWidth: 140, editable: true },
    {
      field: 'colore',
      headerName: 'Color',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      flex: 0.8,
      minWidth: 100,
    },
    { field: 'cost2', headerName: 'Costo', type: 'string', flex: 0.7, minWidth: 100 },
    { field: 'price2', headerName: 'Precio', type: 'string', flex: 0.7, minWidth: 100 },
    { field: 'mprice2', headerName: 'Min precio', type: 'string', flex: 0.7, minWidth: 110 },
  ];
}

function buildMotoresColumnsIncautados(sucursalMap = {}, onBarcode, onEdit, multiempresa) {
  return [
    ...buildMotoresColumns(sucursalMap, onBarcode, onEdit, multiempresa),
    { field: 'nombres', headerName: 'Nombres', type: 'string', flex: 1, minWidth: 130 },
    { field: 'balanceactual', headerName: 'Balance actual', type: 'string', flex: 0.8, minWidth: 120 },
    { field: 'proyectado', headerName: 'Proyectado', type: 'string', flex: 0.8, minWidth: 110 },
  ];
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: FP.shadow.md, transform: 'translateY(-2px)' },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accent,
        },
      }}
    >
      <CardContent sx={{ p: 2.4, '&:last-child': { pb: 2.4 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                color: FP.color.textSecondary,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontSize: '0.66rem',
                mb: 1,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                color: FP.color.textPrimary,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                fontSize: '1.4rem',
                wordBreak: 'break-word',
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              width: 42,
              height: 42,
              borderRadius: 1.4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}14`,
              color: accent,
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// Estado de carga del inventario: anillo girando sobre una pista tenue con
// los tokens FP, centrado en el área de la tabla.
function CargandoInventario() {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2.2}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={56}
          thickness={3.4}
          sx={{ color: FP.color.border }}
        />
        <CircularProgress
          size={56}
          thickness={3.4}
          disableShrink
          sx={{
            color: FP.color.navy,
            position: 'absolute',
            left: 0,
            animationDuration: '900ms',
            '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
          }}
        />
      </Box>
      <Box textAlign="center">
        <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
          Cargando inventario…
        </Typography>
        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.84rem', mt: 0.4 }}>
          Consultando los motores registrados
        </Typography>
      </Box>
    </Box>
  );
}

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

export default function InformeMotores() {
  const [fecha1, setFecha1] = useState("")
  const [fecha2, setFecha2] = useState("")
  const [nosucursal, setNosucursal] = useState({ label: "0-TODO" })
  const [rowsdata, setRowsdata] = useState([])
  const [filteredRowdata, setFilteredRowdata] = useState([])
  const [filteredRowdataTable, setFilteredRowdataTable] = useState([])
  const [opendialog, setOpendialog] = useState(false)

  const [marcas, setMarcas] = useState([])
  const [modelos, setModelos] = useState([])
  const [filteredModelo, setFilteredModelo] = useState([])

  const [status, setStatus] = useState("0")
  const [sucursalnueva, setSucursalnueva] = useState("")
  const [sucursalactual1, setSucursalactual1] = useState("")
  const [chasis, setChasis] = useState("")
  const [sucursal, setSucursal] = useState([])
  const [marca, setMarca] = useState("TODO")
  const [modelo, setModelo] = useState("TODO")
  const [valueInventory, setValueInventory] = useState(0)

  const [loading, setLoading] = useState(false)
  const [openInventarioBulk, setOpenInventarioBulk] = useState(false)
  // Etiquetas: el diálogo trabaja siempre con una lista. Desde el icono de la
  // fila trae un solo chasis; desde el toolbar, todos los seleccionados.
  const [barcodeRows, setBarcodeRows] = useState([])
  const [rowSelection, setRowSelection] = useState([])
  const [barcodeAncho, setBarcodeAncho] = useState("")
  const [barcodeAlto, setBarcodeAlto] = useState("")
  const [savingBarcode, setSavingBarcode] = useState(false)

  const [editOpen, setEditOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [editChasis, setEditChasis] = useState("")
  const [editMarca, setEditMarca] = useState("")
  const [editModelo, setEditModelo] = useState("")
  const [editColore, setEditColore] = useState("")
  const [editCosto, setEditCosto] = useState("")
  const [editPrecio, setEditPrecio] = useState("")
  const [editMprecio, setEditMprecio] = useState("")
  const [editSucursal, setEditSucursal] = useState("")
  const [editCondicion, setEditCondicion] = useState("")
  const [editAno, setEditAno] = useState("")
  const [editCilindraje, setEditCilindraje] = useState("")
  const [savingEdit, setSavingEdit] = useState(false)

  const [controller, dispatch] = useMaterialUIController()
  const { nombreNegocio, roleWeb, roleDetalles, anchoCodigoBarra, altoCodigoBarra } = controller
  const { post } = useApi()
  const { showAlert } = useGlobalMDAlert()
  const puedeModificar = puedeAccion(roleDetalles, "motores", "modificar")
  // Rol con la fila "ocultarcostos": se suprime el costo en grilla, tarjetas,
  // detalle móvil, PDF y edición.
  const sinCostos = ocultaCostos(roleDetalles)
  // Multi-empresa: filtro y columna (solo con el modo activo)
  const multiempresa = useMultiempresa()
  const [empresaFiltro, setEmpresaFiltro] = useState("0")
  // Logo de la empresa filtrada (o la principal) para el encabezado del PDF.
  const logoEmpresa = useLogoEmpresa(empresaFiltro)
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  // Grid y tarjetas se montan según el ancho, no ocultos con display: con
  // miles de chasis, montar las tarjetas en escritorio congelaba la pantalla.
  const esMovil = useMediaQuery(theme.breakpoints.down("md"));
  // Tarjetas móviles por tandas: el resto con «Ver más».
  const [tarjetasVisibles, setTarjetasVisibles] = useState(TARJETAS_POR_TANDA)
  useEffect(() => setTarjetasVisibles(TARJETAS_POR_TANDA), [filteredRowdataTable])
  const navigate = useNavigate();

  const sucursalMap = useMemo(() => {
    const map = {}
    ;(sucursal || []).forEach((s) => {
      const label = s && s.label ? String(s.label) : ''
      if (!label || label === '0-TODO') return
      const code = label.split('-')[0].trim()
      if (code) map[code] = label
    })
    return map
  }, [sucursal])

  const openBarcode = (row) => setBarcodeRows(row ? [row] : [])
  const closeBarcode = () => setBarcodeRows([])
  // Fila de referencia para la vista previa y el encabezado del diálogo.
  const barcodeRow = barcodeRows[0] || null
  const barcodeAbierto = barcodeRows.length > 0

  // Sembrar los campos editables al abrir el diálogo, leyendo siempre los
  // valores actuales del contexto (o localStorage como respaldo). Se hace en un
  // efecto y no dentro de openBarcode porque las columnas del grid memorizan
  // openBarcode y capturarían valores viejos del contexto.
  useEffect(() => {
    if (!barcodeAbierto) return
    // Los valores se persisten en cm (backend/localStorage); la UI los muestra en mm.
    const anchoCmGuardado = Number(anchoCodigoBarra || localStorage.getItem("anchocodigobarra")) || 0
    const altoCmGuardado = Number(altoCodigoBarra || localStorage.getItem("altocodigobarra")) || 0
    setBarcodeAncho(anchoCmGuardado > 0 ? String(anchoCmGuardado * 10) : "")
    setBarcodeAlto(altoCmGuardado > 0 ? String(altoCmGuardado * 10) : "")
  }, [barcodeAbierto, anchoCodigoBarra, altoCodigoBarra])

  // La UI trabaja en milímetros (mm); internamente y en el backend se guarda en cm.
  const anchoMmNum = parseFloat(String(barcodeAncho).replace(',', '.'))
  const altoMmNum = parseFloat(String(barcodeAlto).replace(',', '.'))
  const anchoMmValido = !Number.isNaN(anchoMmNum) && anchoMmNum > 0
  const altoMmValido = !Number.isNaN(altoMmNum) && altoMmNum > 0
  const dimensionesValidas = anchoMmValido && altoMmValido
  const anchoCmNum = anchoMmNum / 10
  const altoCmNum = altoMmNum / 10

  // Módulo fijo de 2 puntos de la impresora térmica (203 dpi, punto de
  // 0.125 mm). Antes el código se estiraba al ancho configurado y el módulo
  // caía en 1.4-1.7 puntos: unas barras salían de 1 punto y otras de 2, y
  // chasis con la misma cantidad de módulos leían o no según dónde cayera el
  // redondeo (y alguno hasta leía un valor falso que pasaba el checksum).
  // Ahora el ancho impreso sale de los módulos de cada código y el ancho
  // configurado es solo el de la etiqueta: con el viewBox real de cada SVG se
  // calcula el ancho mínimo del lote para avisar si el código no cabe.
  const NARROW_PX = 1.8 // debe coincidir con narrow={1.8} de los Barcode128 del diálogo
  const MODULO_MM = (2 * 25.4) / 203
  const PADDING_ETIQUETA_MM = 2 // padding lateral de .etiqueta (1 mm por lado)
  const [anchoMinimoMm, setAnchoMinimoMm] = useState(0)
  useEffect(() => {
    if (!barcodeAbierto) {
      setAnchoMinimoMm(0)
      return
    }
    const zona = document.getElementById('barcode-lote-area')
    if (!zona) return
    let maxPx = 0
    zona.querySelectorAll('svg').forEach((svg) => {
      const vb = String(svg.getAttribute('viewBox') || '').split(/\s+/)
      const w = vb.length === 4 ? parseFloat(vb[2]) : 0
      if (Number.isFinite(w) && w > maxPx) maxPx = w
    })
    setAnchoMinimoMm(maxPx > 0 ? Math.ceil((maxPx / NARROW_PX) * MODULO_MM + PADDING_ETIQUETA_MM) : 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcodeAbierto, barcodeRows])
  const anchoInsuficiente =
    dimensionesValidas && anchoMinimoMm > 0 && anchoMmNum < anchoMinimoMm

  function guardarDimensionesBarcode() {
    if (!dimensionesValidas) {
      showAlert({ message: "El ancho y el alto deben ser números mayores que cero (en mm)", color: "warning", autoHideDuration: 4000 })
      return
    }
    setSavingBarcode(true)
    post('/api/guardarconfigcodigobarra', {
      anchocodigobarra: anchoCmNum,
      altocodigobarra: altoCmNum,
    })
      .then(({ status, data }) => {
        if (status === 200) {
          setAnchoCodigoBarra(dispatch, anchoCmNum)
          setAltoCodigoBarra(dispatch, altoCmNum)
          localStorage.setItem("anchocodigobarra", anchoCmNum)
          localStorage.setItem("altocodigobarra", altoCmNum)
          setSavingBarcode(false)
          showAlert({ message: "Dimensiones del código de barras guardadas correctamente", color: "success", autoHideDuration: 3000 })
        } else {
          setSavingBarcode(false)
          showAlert({ message: data?.Error || "No se pudieron guardar las dimensiones", color: "error", autoHideDuration: 4000 })
        }
      })
      .catch((err) => {
        setSavingBarcode(false)
        showAlert({ message: "Error de red: " + err.message, color: "error", autoHideDuration: 4000 })
      })
  }

  function abrirEditar(row) {
    setEditingProduct(row)
    setEditChasis(row?.id || row?.chasis || "")
    setEditMarca(row?.marca || "")
    setEditModelo(row?.modelo || "")
    setEditColore(row?.colore || "")
    setEditCosto(String(row?.costo ?? row?.cost ?? ""))
    setEditPrecio(String(row?.precio ?? row?.price ?? ""))
    setEditMprecio(String(row?.mprecio ?? row?.mprice ?? ""))
    setEditSucursal(row?.sucursal != null ? String(row.sucursal) : "")
    setEditCondicion((row?.condicion || "").trim())
    setEditAno(row?.ano != null && String(row.ano) !== "0" ? String(row.ano) : "")
    setEditCilindraje(row?.cilindraje != null ? String(row.cilindraje) : "")
    setEditOpen(true)
  }

  function guardarEdicionChasis() {
    if (!editingProduct) return
    const chasisOriginal = editingProduct?.id || editingProduct?.chasis || ""
    if (!editChasis.trim()) {
      alert("El chasis no puede estar vacío")
      return
    }
    const costoNum = parseFloat(editCosto)
    const precioNum = parseFloat(editPrecio)
    const mprecioNum = parseFloat(editMprecio)
    if (Number.isNaN(costoNum) || Number.isNaN(precioNum) || Number.isNaN(mprecioNum)) {
      alert("Costo, precio y precio mínimo deben ser numéricos")
      return
    }
    setSavingEdit(true)
    post('/api/mobile/updatechasis', {
      chasis_original: chasisOriginal,
      chasis: editChasis.trim(),
      marca: editMarca.trim(),
      modelo: editModelo.trim(),
      colore: editColore.trim(),
      costo: costoNum,
      precio: precioNum,
      mprecio: mprecioNum,
      sucursal: editSucursal,
      condicion: editCondicion.trim(),
      ano: editAno.trim(),
      cilindraje: editCilindraje.trim(),
    })
      .then(({ status, data }) => {
        if (status === 200) {
            // Lo que quedó grabado (la marca/modelo pueden salir estandarizados).
            const marcaGuardada = data?.marca ?? editMarca.trim()
            const modeloGuardado = data?.modelo ?? editModelo.trim()
            const updateFn = (l) => (l || []).map((p) => {
              const pid = p?.chasis ?? p?.id
              if (pid === chasisOriginal) {
                return {
                  ...p,
                  id: editChasis.trim(),
                  chasis: editChasis.trim(),
                  marca: marcaGuardada,
                  modelo: modeloGuardado,
                  colore: editColore.trim(),
                  costo: costoNum,
                  cost: costoNum,
                  precio: precioNum,
                  price: precioNum,
                  mprecio: mprecioNum,
                  mprice: mprecioNum,
                  sucursal: editSucursal,
                  condicion: editCondicion.trim(),
                  ano: editAno.trim(),
                  cilindraje: editCilindraje.trim(),
                }
              }
              return p
            })
            setRowsdata((prev) => updateFn(prev))
            setFilteredRowdataTable((prev) => updateFn(prev))
            setFilteredRowdata((prev) => (prev || []).map((c) => (c === chasisOriginal ? editChasis.trim() : c)))
            setEditOpen(false)
            setEditingProduct(null)
            setSavingEdit(false)
            alert(data?.ok || "Chasis actualizado correctamente")
        } else {
          setSavingEdit(false)
          alert(data?.Error || "No se pudo actualizar el chasis")
        }
      })
      .catch((err) => {
        setSavingEdit(false)
        alert("Error de red: " + err.message)
      })
  }

  // Vendido: costo, precio y mínimo no se modifican (el backend también lo valida).
  const editVendido = String(editingProduct?.estado || '').trim().toUpperCase() === 'V'

  const onEditHandler = puedeModificar ? abrirEditar : undefined

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => buildMotoresColumns(sucursalMap, openBarcode, onEditHandler, multiempresa).filter((c) => !(sinCostos && c.field === 'cost2')), [sucursalMap, puedeModificar, multiempresa, sinCostos])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columnsIncautados = useMemo(() => buildMotoresColumnsIncautados(sucursalMap, openBarcode, onEditHandler, multiempresa).filter((c) => !(sinCostos && c.field === 'cost2')), [sucursalMap, puedeModificar, multiempresa, sinCostos])

  // Abre el diálogo de etiquetas con todos los chasis marcados en el grid.
  function abrirEtiquetasSeleccion() {
    const ids = new Set((rowSelection || []).map(String))
    const filas = filteredRowdataTable.filter((r) =>
      ids.has(String(r.id != null ? r.id : r.chasis))
    )
    if (!filas.length) {
      showAlert({ message: 'Seleccione al menos un chasis para imprimir etiquetas', color: 'warning', autoHideDuration: 4000 })
      return
    }
    setBarcodeRows(filas)
  }

  // Clona el <svg> de JsBarcode con dimensiones explícitas. Copiar el innerHTML
  // del contenedor (con width:100%/height:auto) hacía que el código colapsara a
  // tamaño 0 al imprimir.
  function svgEtiqueta(svgEl) {
    const clone = svgEl.cloneNode(true)
    // JsBarcode ya pone xmlns como atributo; lo quitamos para que XMLSerializer no
    // lo declare dos veces.
    clone.removeAttribute('xmlns')
    clone.setAttribute('shape-rendering', 'crispEdges')
    // Ancho siempre por módulos (ver MODULO_MM); el alto es el configurado o,
    // sin configurar, el proporcional al viewBox para que nunca quede en 0.
    const vb = String(clone.getAttribute('viewBox') || '').split(/\s+/)
    if (vb.length === 4) {
      const mmPorPx = MODULO_MM / NARROW_PX
      clone.setAttribute('width', `${(parseFloat(vb[2]) * mmPorPx).toFixed(3)}mm`)
      clone.setAttribute('height', altoMmValido ? `${altoCmNum}cm` : `${(parseFloat(vb[3]) * mmPorPx).toFixed(3)}mm`)
    }
    clone.setAttribute('preserveAspectRatio', 'none')
    clone.style.maxWidth = 'none'
    return new XMLSerializer().serializeToString(clone)
  }

  // Un solo trabajo de impresión para todas las etiquetas del diálogo: los SVG
  // se toman de la zona oculta que renderiza un Barcode128 por chasis.
  function imprimirBarcode() {
    const zona = document.getElementById('barcode-lote-area')
    if (!zona || !barcodeRows.length) return
    const etiquetas = barcodeRows
      .map((row, i) => {
        const svgEl = zona.querySelector(`[data-etiqueta="${i}"] svg`)
        if (!svgEl) return ''
        const meta = [row?.marca, row?.modelo, row?.colore].filter(Boolean).join(' · ')
        // Con multi-empresa activo la etiqueta lleva la empresa dueña del
        // chasis; si no, el nombre del negocio configurado.
        const titulo = multiempresa.activo
          ? nombreEmpresa(multiempresa.empresas, row?.empresa || 1)
          : (nombreNegocio || '')
        return `<div class="etiqueta">` +
          `<div class="title">${escHtml(titulo)}</div>` +
          `<div class="meta">${escHtml(meta)}</div>` +
          `${svgEtiqueta(svgEl)}` +
          `</div>`
      })
      .filter(Boolean)
      .join('')
    if (!etiquetas) return
    const titulo = barcodeRows.length === 1
      ? `Código de barras ${String(barcodeRows[0]?.id || barcodeRows[0]?.chasis || '')}`
      : `Etiquetas de chasis (${barcodeRows.length})`
    imprimirHtml(
      `<!doctype html><html><head><meta charset="utf-8"><meta name="color-scheme" content="light">` +
      `<title>${escHtml(titulo)}</title>` +
      `<style>body{margin:0;padding:0;background:#fff;color:#000;font-family:Arial,sans-serif;}` +
      // page-break-inside evita que una etiqueta se parta entre páginas: en
      // impresora de rollo cae una por página, en hoja entran varias.
      // Márgenes mínimos: en un rollo de 60 mm, el margen de página de 10 mm +
      // padding de 24px que había antes dejaban ~27 mm reales para el código y
      // las barras salían demasiado finas para el escáner.
      `.etiqueta{padding:2mm 1mm;display:flex;flex-direction:column;align-items:center;justify-content:center;` +
      `page-break-inside:avoid;break-inside:avoid;}` +
      `.meta{font-size:12px;color:#475569;margin-bottom:6px;text-align:center}` +
      `.title{font-size:14px;font-weight:700;margin-bottom:4px;text-align:center}` +
      `svg{display:block}` +
      `@media print { @page { margin: 0; } }</style></head>` +
      `<body>${etiquetas}</body></html>`
    )
  }

  function clicktransferir(sucursalact, chasis) {
    setChasis(chasis)
    setOpendialog(true)
  }

  useEffect(() => {
    setLoading(true)
    post('/api/informemotores/inventario', { categoria: '0' })
      .then(function ({ status, data }) {
        if (status === 200) {
            setSucursal([{ label: "0-TODO" }, ...(data["sucursales"] || [])])
            const productos = (data["productos"] || []).map(normalizarProducto)
            setRowsdata(productos)
            setFilteredRowdataTable(productos)
            setFilteredRowdata(productos.map(item => item.chasis))

            // Marcas y modelos salen del propio inventario: el endpoint viejo
            // los traía con 3 consultas extra sobre producto.
            const marcasUnicas = [...new Set(productos.map(p => p.marca).filter(Boolean))].sort()
            setMarcas(["TODO", ...marcasUnicas])
            const paresVistos = new Set()
            const listmodelo = []
            productos.forEach(p => {
              if (!p.modelo) return
              const clave = `${p.marca}|${p.modelo}`
              if (paresVistos.has(clave)) return
              paresVistos.add(clave)
              listmodelo.push({ name: p.marca, modelo: p.modelo })
            })
            setModelos(listmodelo)
        }
        if (status === 400) {
            alert("Error")
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))

  }, []);

  function procesarrequerimiento(event) {
    var newList = rowsdata;

    if (nosucursal["label"] != "0-TODO") {
      newList = rowsdata.filter(item => item.sucursal == nosucursal["label"].split("-")[0])
      setFilteredRowdataTable(newList)
      setFilteredRowdata(newList.map(item => item.chasis))
    }
    else {
      setFilteredRowdataTable(rowsdata)
      setFilteredRowdata(rowsdata.map(item => item.chasis))
    }

    if (marca != "TODO") {
      newList = newList.filter(item => item.marca == marca)
      setFilteredRowdataTable(newList)
      setFilteredRowdata(newList.map(item => item.chasis))
    }

    if (modelo != "TODO") {
      newList = newList.filter(item => item.modelo == modelo)
      setFilteredRowdataTable(newList)
      setFilteredRowdata(newList.map(item => item.chasis))
    }
  }

  function getInventorybycategory(categoria, empresaSel = empresaFiltro) {
    setStatus(categoria)
    setNosucursal({ label: "0-TODO" })
    setMarca("TODO")
    setModelo("TODO")
    setFilteredRowdataTable([])
    setLoading(true)
    post('/api/informemotores/inventario', {
      'categoria': categoria,
      ...(multiempresa.activo ? { empresa: Number(empresaSel) } : {}),
    })
      .then(function ({ status, data }) {
        if (status === 200) {
            const productos = (data["productos"] || []).map(normalizarProducto)
            setRowsdata(productos)
            setFilteredRowdataTable(productos)
            setFilteredRowdata(productos.map(item => item.chasis))
            setLoading(false)
        }
        if (status === 400) {
            setRowsdata([])
            alert(data["Error"])
            setLoading(false)
        }
      })
      .catch(function () {
        setLoading(false)
      })
  }

  function modificarsucursal() {
    let aerror = false
    post('/api/modificasucursal', { 'sucursalacambiar': sucursalnueva, 'sucursalactual': sucursalactual1, 'chasis': chasis })
      .then(function ({ status, data }) {
        if (status === 200) {
            setOpendialog(false)
            alert(data["ok"])
        }
        if (status === 400) {
            alert(data["Error"])
        }
      })
  }

  function alertar(cellValues) {
    alert(cellValues)
  }

  // Reporte en PDF (jsPDF + autotable) y Excel: ver reporteinventariomotores.js.
  const opcionesReporte = () => ({
    nombreNegocio,
    logoEmpresa,
    sinCostos,
    incautados: status === '1',
    multiempresa,
    sucursalMap,
    status,
    nosucursal,
    marca,
    modelo,
    empresaFiltro,
  });

  function handlepdf() {
    if (filteredRowdataTable.length === 0) {
      alert("No hay datos para procesar este reporte");
      return;
    }
    try {
      generarPdfInventario(filteredRowdataTable, opcionesReporte());
    } catch (e) {
      alert("No se pudo generar el PDF: " + (e?.message || e));
    }
  }

  function handleExcel() {
    if (filteredRowdataTable.length === 0) {
      alert("No hay datos para procesar este reporte");
      return;
    }
    try {
      exportarExcelInventario(filteredRowdataTable, opcionesReporte());
    } catch (e) {
      alert("No se pudo generar el Excel: " + (e?.message || e));
    }
  }

  function buscarChasis() {
    if (chasis == null || chasis == "") {
      // noop
    } else {
      var list = rowsdata.filter(item => item.chasis == chasis)
      setFilteredRowdataTable(list)
    }
  }

  const totalCosto = filteredRowdataTable.reduce((sum, row) => sum + (parseFloat(String(row.costo ?? row.cost ?? 0).replace(",", "").trim()) || 0), 0);
  const totalValor = filteredRowdataTable.reduce((sum, row) => sum + (parseFloat(String(row.precio ?? row.price ?? 0).replace(",", "").trim()) || 0), 0);

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        bgcolor: FP.color.surface,
      }}
    >
      <PageHeader
        title="Motores"
        subtitle="Informe de Motores"
        cta={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<QrCodeScannerRoundedIcon />}
              onClick={() => navigate('/inventario/conteos')}
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                px: 2,
                height: 38,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Conteo por Sucursal
            </Button>
            {/* Con "ocultarcostos" no se ofrece: el alta bulk registra la
                factura de compra con sus costos. */}
            {!sinCostos && (
              <Button
                variant="outlined"
                startIcon={<UploadFileRoundedIcon />}
                onClick={() => setOpenInventarioBulk(true)}
                sx={{
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                  bgcolor: '#FFFFFF',
                  borderRadius: 1.4,
                  px: 2,
                  height: 38,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                }}
              >
                Inventario bulk
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<FileDownloadRoundedIcon />}
              disabled={filteredRowdataTable.length === 0}
              onClick={handleExcel}
              sx={{
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                borderRadius: 1.4,
                px: 2.2,
                height: 38,
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
              }}
            >
              Exportar Excel
            </Button>
          </Box>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de Búsqueda
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600 }}>
              Sucursal · Marca · Modelo · Tipo · Chasis
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  id="sucursal-autocomplete"
                  options={sucursal || []}
                  value={nosucursal || null}
                  getOptionLabel={(opt) => (opt && opt.label) || ""}
                  isOptionEqualToValue={(opt, val) => (opt && val && opt.label) === (val && val.label)}
                  onChange={(event, newValue) => setNosucursal(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  id="marca-autocomplete"
                  options={marcas || []}
                  value={marca || null}
                  onChange={(event, value) => {
                    setMarca(value || "TODO");
                    setModelo("TODO");
                    const list = (modelos || []).filter(item => item.name === value);
                    const modelolist = list.map(item => item.modelo);
                    modelolist.unshift("TODO");
                    setFilteredModelo(modelolist);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Marcas"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  id="modelo-autocomplete"
                  options={filteredModelo || []}
                  value={modelo || null}
                  onChange={(event, value) => setModelo(value || "TODO")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Modelos"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={INPUT_SX}>
                  <InputLabel id="tipo-select-label">Tipo</InputLabel>
                  <Select
                    labelId="tipo-select-label"
                    id="tipo-select"
                    label="Tipo"
                    value={status}
                    onChange={(event) => getInventorybycategory(event.target.value)}
                  >
                    <MenuItem value="0">Todos</MenuItem>
                    <MenuItem value="1">Incautados</MenuItem>
                    <MenuItem value="2">Nuevo</MenuItem>
                    <MenuItem value="3">Usado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {multiempresa.activo && (
                <Grid item xs={12} sm={6} md={3}>
                  <SelectorEmpresa
                    value={empresaFiltro}
                    onChange={(v) => {
                      setEmpresaFiltro(v);
                      getInventorybycategory(status, v);
                    }}
                    empresas={multiempresa.empresas}
                    sx={INPUT_SX}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={9}>
                <Autocomplete
                  disablePortal
                  id="chasis-autocomplete"
                  options={filteredRowdata || []}
                  filterOptions={filtrarOpcionesChasis}
                  value={chasis || null}
                  onChange={(event, value) => setChasis(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Chasis"
                      fullWidth
                      placeholder="Buscar por chasis…"
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => buscarChasis()}
                  fullWidth
                  sx={{
                    height: 46,
                    borderColor: FP.color.border,
                    color: FP.color.textPrimary,
                    bgcolor: '#FFFFFF',
                    borderRadius: 1.6,
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                  }}
                >
                  Buscar Chasis
                </Button>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarrequerimiento()}
                disabled={loading}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontSize: '0.94rem',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                }}
              >
                {loading ? 'Procesando…' : 'Procesar'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {filteredRowdataTable.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total de registros"
                value={String(filteredRowdataTable.length)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            {!sinCostos && (
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  label="Total costo"
                  value={`$${totalCosto.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  icon={PaymentIcon}
                  accent={FP.color.successDark}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total precio"
                value={`$${totalValor.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={AttachMoneyIcon}
                accent={FP.color.navy}
              />
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Inventario de Motores
            </Typography>
            {filteredRowdataTable.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadRoundedIcon />}
                onClick={handleExcel}
                sx={{
                  borderColor: FP.color.success,
                  color: FP.color.success,
                  borderRadius: 1.2,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.successSoft, borderColor: FP.color.success },
                }}
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handlepdf()}
                sx={{
                  borderColor: FP.color.danger,
                  color: FP.color.danger,
                  borderRadius: 1.2,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                }}
              >
                Generar PDF
              </Button>
              </Box>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {loading ? (
              <CargandoInventario />
            ) : filteredRowdataTable.length > 0 ? (
              <>
                {!esMovil && (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <DataGrid
                    rows={filteredRowdataTable}
                    columns={status === '1' ? columnsIncautados : columns}
                    getRowId={(row) => (row.id != null ? String(row.id) : row.chasis)}
                    slots={{ toolbar: CustomToolbar }}
                    slotProps={{
                      toolbar: {
                        seleccionados: rowSelection.length,
                        onImprimirEtiquetas: abrirEtiquetasSeleccion,
                      },
                    }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    checkboxSelection
                    rowSelectionModel={rowSelection}
                    onRowSelectionModelChange={setRowSelection}
                    disableRowSelectionOnClick
                    rowHeight={42}
                    sx={{ minWidth: { xs: 720, sm: '100%' }, height: 520, ...fpDataGridSx }}
                    autoHeight={false}
                  />
                </Box>
                )}
                {esMovil && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                  {filteredRowdataTable.slice(0, tarjetasVisibles).map((row, idx) => (
                    <Box
                      key={row.id != null ? String(row.id) : `card-${row.chasis ?? idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: '#FFFFFF',
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1rem', lineHeight: 1.2, wordBreak: 'break-word' }}>
                            {row.marca || ''} {row.modelo || ''}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', mt: 0.25, wordBreak: 'break-word' }}>
                            Chasis: {row.id || row.chasis || '—'}
                          </Typography>
                          {row.colore ? (
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.74rem' }}>
                              Color: {row.colore}
                            </Typography>
                          ) : null}
                        </Box>
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {row.sucursal ? (
                            <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                              {row.sucursal}
                            </Box>
                          ) : null}
                          <Tooltip title="Código de barras">
                            <IconButton size="small" onClick={() => openBarcode(row)} sx={{ color: FP.color.navy }}>
                              <QrCode2RoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 1 }}>
                        {[
                          ...(sinCostos ? [] : [['Costo', row.cost2]]),
                          ['Precio', row.price2],
                          ['Min precio', row.mprice2],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.92rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                              {value ?? '—'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {status === '1' && row.nombres ? (
                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${FP.color.border}` }}>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                            Cliente
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.88rem', fontWeight: 600 }}>
                            {row.nombres}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                              Balance: <strong>{row.balanceactual ?? '—'}</strong>
                            </Typography>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                              Proyect.: <strong>{row.proyectado ?? '—'}</strong>
                            </Typography>
                          </Box>
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                  {filteredRowdataTable.length > tarjetasVisibles && (
                    <Button
                      variant="outlined"
                      onClick={() => setTarjetasVisibles((n) => n + TARJETAS_POR_TANDA)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Ver más ({tarjetasVisibles} de {filteredRowdataTable.length})
                    </Button>
                  )}
                </Box>
                )}
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Seleccione el tipo de inventario o los filtros y haga clic en "Procesar" para obtener resultados.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={opendialog}
          onClose={() => setOpendialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle id="alert-dialog-title">
            Digite la sucursal a transferir
          </DialogTitle>
          <DialogContent>
            <TextField
              variant="outlined"
              fullWidth
              label="Sucursal"
              value={sucursalnueva}
              onChange={(event) => setSucursalnueva(event.target.value)}
              sx={{ mt: 1, '& .MuiOutlinedInput-root': { height: '45px' } }}
            />
            <DialogActions sx={{ mt: 2 }}>
              <Button variant="contained" sx={{ bgcolor: FP.color.navy, color: '#FFFFFF', '&:hover': { bgcolor: FP.color.navyDeep } }} onClick={() => modificarsucursal()}>Aceptar</Button>
              <Button variant="outlined" onClick={() => setOpendialog(false)}>Cancelar</Button>
            </DialogActions>
          </DialogContent>
        </Dialog>

        <Dialog
          open={editOpen}
          onClose={() => !savingEdit && setEditOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
            Editar chasis
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Chasis"
                  value={editChasis}
                  onChange={(e) => setEditChasis(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  disablePortal
                  freeSolo
                  options={(marcas || []).filter((m) => m !== 'TODO')}
                  value={editMarca || null}
                  onChange={(_, v) => setEditMarca(v || '')}
                  onInputChange={(_, v) => setEditMarca(v || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Marca" fullWidth InputLabelProps={{ shrink: true }} sx={fpInputSx} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  disablePortal
                  freeSolo
                  options={(modelos || []).filter((m) => m && m !== 'TODO').map((m) => (typeof m === 'string' ? m : m.name || m.modelo || ''))}
                  value={editModelo || null}
                  onChange={(_, v) => setEditModelo(v || '')}
                  onInputChange={(_, v) => setEditModelo(v || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Modelo" fullWidth InputLabelProps={{ shrink: true }} sx={fpInputSx} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Color"
                  value={editColore}
                  onChange={(e) => setEditColore(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              {!sinCostos && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Costo"
                    type="number"
                    value={editCosto}
                    onChange={(e) => setEditCosto(e.target.value)}
                    disabled={editVendido}
                    helperText={editVendido ? "Chasis vendido" : undefined}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={fpInputSx}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Precio"
                  type="number"
                  value={editPrecio}
                  onChange={(e) => setEditPrecio(e.target.value)}
                  disabled={editVendido}
                  helperText={editVendido ? "Chasis vendido" : undefined}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Precio mínimo"
                  type="number"
                  value={editMprecio}
                  onChange={(e) => setEditMprecio(e.target.value)}
                  disabled={editVendido}
                  helperText={editVendido ? "Chasis vendido" : undefined}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Condición"
                  value={editCondicion}
                  onChange={(e) => setEditCondicion(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="Nuevo">Nuevo</MenuItem>
                  <MenuItem value="Usado">Usado</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Año"
                  type="number"
                  value={editAno}
                  onChange={(e) => setEditAno(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Cilindraje"
                  value={editCilindraje}
                  onChange={(e) => setEditCilindraje(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  options={(sucursal || []).filter((s) => s && s.label && s.label !== '0-TODO')}
                  getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.label || '')}
                  value={(sucursal || []).find((s) => s?.label && s.label.split('-')[0].trim() === String(editSucursal).trim()) || null}
                  onChange={(_, v) => setEditSucursal(v?.label ? v.label.split('-')[0].trim() : '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Sucursal" fullWidth InputLabelProps={{ shrink: true }} sx={fpInputSx} />
                  )}
                />
              </Grid>
            </Grid>
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button variant="outlined" onClick={() => setEditOpen(false)} disabled={savingEdit}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={guardarEdicionChasis}
                disabled={savingEdit}
                sx={{ bgcolor: FP.color.navy, color: '#FFFFFF', '&:hover': { bgcolor: FP.color.navyDeep } }}
              >
                {savingEdit ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>

        <InventariobulkModal
          open={openInventarioBulk}
          onClose={() => setOpenInventarioBulk(false)}
        />

        <Dialog
          open={barcodeAbierto}
          onClose={closeBarcode}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {barcodeRows.length > 1 ? `Etiquetas de chasis (${barcodeRows.length})` : 'Código de Barras'}
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                {barcodeRows.length > 1
                  ? 'Se imprimen todas en un solo trabajo de impresión'
                  : [barcodeRow?.marca, barcodeRow?.modelo, barcodeRow?.colore].filter(Boolean).join(' · ') || '—'}
              </Typography>
            </Box>
            <IconButton onClick={closeBarcode} sx={{ p: 1 }}>
              <CancelIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box
              id="barcode-preview-area"
              sx={{
                p: 2,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'auto',
              }}
            >
              {barcodeRow && (
                <Barcode128
                  value={String(barcodeRow.id || barcodeRow.chasis || '')}
                  height={80}
                  narrow={1.8}
                  fontSize={16}
                  heightCm={altoMmValido ? altoCmNum : 0}
                  moduloMm={MODULO_MM}
                />
              )}
            </Box>

            {barcodeRows.length > 1 && (
              <Typography sx={{ mt: 1, color: FP.color.textSecondary, fontSize: '0.76rem', textAlign: 'center' }}>
                Vista previa de la primera etiqueta · {barcodeRows.length - 1} más en el lote
              </Typography>
            )}

            {/* Zona oculta: un código por chasis del lote. De aquí toma
                imprimirBarcode() los SVG ya generados por JsBarcode. */}
            <Box
              id="barcode-lote-area"
              aria-hidden="true"
              sx={{ position: 'absolute', left: -10000, top: 0, width: 800, pointerEvents: 'none' }}
            >
              {barcodeRows.map((row, i) => (
                <div key={`${row.id != null ? row.id : row.chasis}-${i}`} data-etiqueta={i}>
                  <Barcode128
                    value={String(row.id || row.chasis || '')}
                    height={80}
                    narrow={1.8}
                    fontSize={16}
                    heightCm={altoMmValido ? altoCmNum : 0}
                    moduloMm={MODULO_MM}
                  />
                </div>
              ))}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 700, mb: 1 }}>
                Dimensiones de impresión (mm)
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.72rem', mb: 1 }}>
                {anchoMinimoMm > 0
                  ? `Ancho de etiqueta que necesita este lote: ${anchoMinimoMm} mm · alto ≥ 12 mm. El código se imprime a su tamaño, no se estira.`
                  : 'Recomendado para lectura con escáner: alto ≥ 12 mm.'}
              </Typography>
              {anchoInsuficiente ? (
                <Typography sx={{ color: FP.color.danger, fontSize: '0.72rem', fontWeight: 700, mb: 1 }}>
                  En una etiqueta de {anchoMmNum} mm el código más largo de este lote no cabe
                  y se cortará. Use etiquetas de al menos {anchoMinimoMm} mm.
                </Typography>
              ) : null}
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Ancho (mm)"
                    type="number"
                    fullWidth
                    size="small"
                    value={barcodeAncho}
                    onChange={(e) => setBarcodeAncho(e.target.value)}
                    error={barcodeAncho !== "" && !anchoMmValido}
                    inputProps={{ min: 0, step: 1 }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Alto (mm)"
                    type="number"
                    fullWidth
                    size="small"
                    value={barcodeAlto}
                    onChange={(e) => setBarcodeAlto(e.target.value)}
                    error={barcodeAlto !== "" && !altoMmValido}
                    inputProps={{ min: 0, step: 1 }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={!dimensionesValidas || savingBarcode}
                    onClick={guardarDimensionesBarcode}
                    sx={{
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      height: 40,
                      color: FP.color.navy,
                      borderColor: FP.color.navy,
                      '&:hover': { borderColor: FP.color.navyDeep, bgcolor: `${FP.color.navy}0A` },
                      '&.Mui-disabled': { color: FP.color.textSecondary, borderColor: FP.color.border },
                    }}
                  >
                    {savingBarcode ? 'Guardando…' : 'Guardar dimensiones'}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button
                variant="outlined"
                onClick={closeBarcode}
                sx={{
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: FP.color.textPrimary,
                  borderColor: FP.color.borderStrong,
                  '&:hover': { borderColor: FP.color.textSecondary, bgcolor: FP.color.surface },
                }}
              >
                Cerrar
              </Button>
              <Button
                variant="contained"
                startIcon={<PrintRoundedIcon />}
                onClick={imprimirBarcode}
                sx={{
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.navyDeep },
                }}
              >
                {barcodeRows.length > 1 ? `Imprimir ${barcodeRows.length} etiquetas` : 'Imprimir'}
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
}
